/**
 * Cart Header Component
 */

'use client';

import styled from '@emotion/styled';
import { Icon, Text } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n/client';

const Header = styled.div`
  height: 69px;
  width: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  padding: ${({ theme }) => theme.space.md};
  box-sizing: border-box;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
`;

const Title = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};

  p {
    font-size: ${({ theme }) => theme.fontSizes[6]}px;
  }
`;

const CloseButton = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  padding: ${({ theme }) => theme.space.sm};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    pointer-events: none;
  }

  &:hover {
    opacity: 0.7;
  }
`;

const ShoppingBagWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ItemsBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  min-width: 18px;
  height: 18px;
  border-radius: ${({ theme }) => theme.radius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${({ theme }) => theme.space.xs};
`;

export interface CartHeaderProps {
  itemCount: number;
  onClose: () => void;
}

export function CartHeader({ itemCount, onClose }: CartHeaderProps) {
  const { t } = useTranslation('sidecart');

  return (
    <Header>
      <CloseButton
        data-testid="close-cart-button"
        onClick={onClose}
        title={t('cart.close')}
        aria-label={t('cart.close')}
      >
        <Icon.PDP.ChevronLeft
          width="20"
          height="20"
          testId="close-side-cart"
        />
      </CloseButton>

      <Title>
        <Text variant="body" weight="bold">
          {t('cart.title')}
        </Text>
        <Text variant="body" weight="regular">
          {t('cart.items', { count: itemCount })}
        </Text>
      </Title>

      <ShoppingBagWrapper>
        <Icon.PDP.ShoppingBag width={24} height={24} />
        {itemCount > 0 && <ItemsBadge>{itemCount}</ItemsBadge>}
      </ShoppingBagWrapper>
    </Header>
  );
}
